import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { fetchPageBySlug } from '../lib/cms';
import Gallery from '../components/Gallery';
import CarDecorationGallery from '../components/CarDecorationGallery';

export default function DekorationPage({ forcedType }) {
  const params = useParams();
  const type = forcedType || params.type || 'bil';
  const pageSlug = `dekoration-${type}`;

  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);

  // Metadata og defaults per type hvis CMS er under indlæsning
  const typeInfo = {
    lastbil: {
      defaultTitle: 'Lastbildekoration',
      badge: 'Vognmandsreklamer & Tunge Køretøjer',
      heroDefaultImg: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-9103.jpeg',
      icon: 'fa-solid fa-truck'
    },
    bil: {
      defaultTitle: 'Bildekoration & Indpakning',
      badge: 'Firmabiler & Car Wrapping',
      heroDefaultImg: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-7762.jpeg',
      icon: 'fa-solid fa-car'
    },
    vaeg: {
      defaultTitle: 'Vægfolie & Grafisk Dekoration',
      badge: 'Indendørs Rumudsmykning & Skilte',
      heroDefaultImg: 'https://assets.auxodev.dk/assets/83c5e513-aae5-4fee-8b74-db63d3cee346/img-1963.jpeg',
      icon: 'fa-solid fa-paintbrush'
    }
  };

  const currentInfo = typeInfo[type] || typeInfo.bil;

  useEffect(() => {
    async function loadPage() {
      setLoading(true);
      const data = await fetchPageBySlug(pageSlug);
      setPage(data);
      setLoading(false);
    }
    loadPage();
  }, [pageSlug]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-brand-red border-t-transparent" />
      </div>
    );
  }

  const heroImage =
    (page?.hero_billede && (Array.isArray(page.hero_billede) ? page.hero_billede[0] : page.hero_billede)) ||
    (page?.hero_image && (Array.isArray(page.hero_image) ? page.hero_image[0] : page.hero_image)) ||
    (page?.billeder && page.billeder[0]) ||
    (page?.images && page.images[0]) ||
    currentInfo.heroDefaultImg;
  const heroImageAlt = page?.hero_billede_alt || page?.hero_image_alt || page?.title || currentInfo.defaultTitle;
  const secondaryImage =
    (page?.mellem_billede && (Array.isArray(page.mellem_billede) ? page.mellem_billede[0] : page.mellem_billede)) ||
    (page?.sekundaert_billede && (Array.isArray(page.sekundaert_billede) ? page.sekundaert_billede[0] : page.sekundaert_billede)) ||
    (page?.secondary_image && (Array.isArray(page.secondary_image) ? page.secondary_image[0] : page.secondary_image)) ||
    (page?.billeder && page.billeder[1]) ||
    (page?.images && page.images[1]) ||
    (page?.billeder && page.billeder[0]) ||
    (page?.images && page.images[0]) ||
    currentInfo.heroDefaultImg;
  const secondaryImageAlt =
    page?.mellem_billede_alt ||
    page?.sekundaert_billede_alt ||
    page?.secondary_image_alt ||
    page?.title ||
    currentInfo.defaultTitle;

  return (
    <div className="pb-24 space-y-20 lg:space-y-24 bg-slate-50/40">
      <Helmet>
        <title>{page?.meta_titel || page?.meta_title || `${currentInfo.defaultTitle} - CRE8IT`}</title>
        <meta name="description" content={page?.meta_beskrivelse || page?.meta_description || `${currentInfo.defaultTitle} hos CRE8IT. Professionelle folieløsninger tilpasset dine behov.`} />
      </Helmet>

      {/* 1. Hero Sektion */}
      <section className="relative overflow-hidden bg-[#325d52] py-16 lg:py-24 border-b border-[#245b53]/40 text-white">
        {/* Subtile dekorative lysglød */}
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-96 w-96 rounded-full bg-[#deecd4]/15 blur-3xl" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center">
            
            {/* Venstre kolonne: Tekst */}
            <div className="space-y-6 lg:col-span-7">
              <div className="inline-flex items-center gap-2 rounded-full bg-[#deecd4] px-3.5 py-1.5 text-xs font-bold text-[#325d52] border border-[#deecd4] shadow-xs">
                <i className={currentInfo.icon} />
                <span>{currentInfo.badge}</span>
              </div>

              <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl leading-[1.15]">
                {page?.overskrift || page?.headline || (
                  <>
                    {currentInfo.defaultTitle.split(' ')[0]}{' '}
                    <span className="text-[#deecd4]">
                      {currentInfo.defaultTitle.split(' ').slice(1).join(' ')}
                    </span>
                  </>
                )}
              </h1>
              <div className="flex items-center gap-1.5 pt-0.5">
                <div className="h-1.5 w-12 rounded-full bg-[#deecd4]" />
                <div className="h-1.5 w-3 rounded-full bg-white/40" />
                <div className="h-1.5 w-1.5 rounded-full bg-[#deecd4]" />
              </div>

              <p className="text-lg text-emerald-50/90 leading-relaxed max-w-2xl">
                {page?.underoverskrift || page?.subtitle || 'Vi designer, producerer og monterer professionelle dekorationsløsninger i højeste kvalitet.'}
              </p>
              
              <div className="flex flex-wrap gap-4 pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#deecd4] px-6 py-3.5 text-sm font-bold text-[#1b453f] shadow-md transition-all hover:bg-white hover:text-gray-900 cursor-pointer"
                >
                  <i className="fa-solid fa-envelope text-xs text-[#1b453f]" />
                  Få et uforpligtende tilbud
                </Link>
                <a
                  href="#galleri"
                  className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-white/60 bg-white/15 backdrop-blur-xs px-6 py-3.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-white hover:text-gray-900 cursor-pointer group"
                >
                  <i className="fa-solid fa-images text-[#deecd4] group-hover:text-gray-900 transition-colors" />
                  Se vores arbejde
                </a>
              </div>
            </div>

            {/* Højre kolonne: Billede */}
            <div className="relative lg:col-span-5">
              <div className="aspect-[4/3] sm:aspect-square overflow-hidden rounded-3xl border border-gray-200 shadow-xl bg-white">
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/${heroImage.replace('https://assets.auxodev.dk/', '')}`}
                  alt={heroImageAlt}
                  className="h-full w-full object-cover"
                />
              </div>

              {/* Dekorativ badge */}
              <div className="absolute -bottom-5 -right-5 rounded-2xl bg-white p-4 shadow-xl border border-gray-100 hidden sm:flex items-center gap-3">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-green-light/40 text-brand-red">
                  <i className="fa-solid fa-certificate text-xl" />
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Garanti & Holdbarhed</p>
                  <p className="text-sm font-bold text-gray-900">Premium foliekvalitet</p>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Undernavigation mellem de tre undersider for hurtig adgang */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex flex-wrap items-center justify-center gap-3 p-2 bg-white rounded-2xl border border-gray-200/80 shadow-xs">
          <span className="text-xs font-bold uppercase tracking-wider text-gray-400 px-3 py-1 hidden sm:inline-block">
            Dekorationsområder:
          </span>
          <Link
            to="/dekoration/bil"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              type === 'bil'
                ? 'bg-brand-red text-white shadow-xs'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <i className="fa-solid fa-car text-xs" /> Bil
          </Link>
          <Link
            to="/dekoration/lastbil"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              type === 'lastbil'
                ? 'bg-brand-red text-white shadow-xs'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <i className="fa-solid fa-truck text-xs" /> Lastbil
          </Link>
          <Link
            to="/dekoration/vaeg"
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all ${
              type === 'vaeg'
                ? 'bg-brand-red text-white shadow-xs'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <i className="fa-solid fa-paintbrush text-xs" /> Væg
          </Link>
        </div>
      </section>

      {/* 2. Detaljeret Præsentation & Brødtekst */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:items-center rounded-3xl border border-gray-200/90 bg-white p-6 sm:p-8 lg:p-12 shadow-xs">
          
          {/* Venstre side: Sekundært billede */}
          <div className="lg:col-span-5 order-last lg:order-first">
            <div className="relative p-1.5 sm:p-2 rounded-3xl bg-gradient-to-tr from-[#1b453f] via-[#245b53] to-[#deecd4] shadow-xl shadow-[#245b53]/20">
              <div className="aspect-[4/3] sm:aspect-[4/5] overflow-hidden rounded-[18px] bg-white">
                <img
                  src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/${secondaryImage.replace('https://assets.auxodev.dk/', '')}`}
                  alt={secondaryImageAlt}
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
          </div>

          {/* Højre side: Brødtekst */}
          <div className="lg:col-span-7 space-y-6">
            <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Faglig præcision</span>
            <h2 className="text-3xl font-bold text-gray-900 sm:text-4xl">
              Professionelle løsninger fra <span className="text-brand-red">idé til montering</span>
            </h2>
            <div className="flex items-center gap-1.5 pt-0.5">
              <div className="h-1 w-10 rounded-full bg-brand-red" />
              <div className="h-1 w-2.5 rounded-full bg-brand-green-light" />
            </div>
            
            <div className="prose max-w-none">
              {page?.indhold || page?.content ? (
                <div dangerouslySetInnerHTML={{ __html: page.indhold || page.content }} />
              ) : (
                <p>
                  Vi leverer skræddersyede folieløsninger, der opfylder dine krav til holdbarhed, æstetik og synlighed. Kontakt os for en snak om mulighederne.
                </p>
              )}
            </div>
          </div>

        </div>
      </section>

      {/* 3. Specifikationer & Fordele */}
      {(page?.specifikationer || page?.specifications) && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl border border-gray-200/90 bg-white p-8 sm:p-12 shadow-xs space-y-6">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-green-light/30 text-brand-red">
                <i className="fa-solid fa-list-check" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900">Fordele & Specifikationer</h3>
            </div>
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: page.specifikationer || page.specifications }} />
          </div>
        </section>
      )}

      {/* 4. Galleri Sektion */}
      <section id="galleri" className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 border-t border-gray-200/80 pt-16 space-y-8">
        <div className="text-center max-w-2xl mx-auto space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-brand-red">Inspiration & Referencer</span>
          <h2 className="text-3xl font-bold text-gray-900">Se vores udførte opgaver</h2>
        </div>

        {/* Henter galleri baseret på type */}
        {type === 'bil' ? (
          <CarDecorationGallery />
        ) : (
          <Gallery slug={pageSlug} />
        )}
      </section>
    </div>
  );
}
